import React, { useState, useMemo } from "react";
import { FaFileExport, FaSpinner, FaTrash } from "react-icons/fa";

function ExportModal({ fields, onExport, onCancel, loading }) {
    const [search, setSearch] = useState("");
    const [selectedFields, setSelectedFields] = useState([]);
    const [format, setFormat] = useState("xlsx");

    const availableFields = useMemo(() => {
        return fields.filter(f => {
            const label = f.string ? `${f.string} (${f.name})` : f.name || f;
            return (
                label.toLowerCase().includes(search.toLowerCase()) &&
                !selectedFields.find(sf => sf.value === (f.name || f))
            );
        }).map(f => ({
            value: f.name || f,
            label: f.string ? `${f.string}` : f,
        }));
    }, [fields, search, selectedFields]);

    const handleAddField = (field) => {
        setSelectedFields(prev => [...prev, field]);
    };

    const handleRemoveField = (fieldValue) => {
        setSelectedFields(prev => prev.filter(f => f.value !== fieldValue));
    };

    const allSelected = selectedFields.length === fields.length;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 min-w-[700px] max-w-[90vw] flex flex-col gap-6 border border-gray-200">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-800">Exporter les données</h2>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
                </div>

                {/* Format */}
                <div className="flex items-center gap-6">
                    <div className="w-1/2">
                        <label className="font-semibold block mb-1">Format d'exportation :</label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="format" value="xlsx" checked={format === "xlsx"} onChange={() => setFormat("xlsx")} />
                                XLSX
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="format" value="csv" checked={format === "csv"} onChange={() => setFormat("csv")} />
                                CSV
                            </label>
                        </div>
                    </div>
                </div>

                {/* Contenu */}
                <div className="flex gap-4">
                    {/* Champs disponibles */}
                    <div className="w-1/2 border rounded p-4">
                        <h4 className="font-semibold mb-2">Champs disponibles</h4>
                        <input
                            type="text"
                            className="w-full px-2 py-1 border rounded mb-2"
                            placeholder="Rechercher un champ..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <ul className="max-h-48 overflow-auto text-sm">
                            {availableFields.map(field => (
                                <li
                                    key={field.value}
                                    className="flex justify-between items-center py-1 border-b hover:bg-gray-50"
                                >
                                    <span>{field.label}</span>
                                    <button
                                        className="text-green-600 text-sm"
                                        onClick={() => handleAddField(field)}
                                    >Ajouter +</button>
                                </li>
                            ))}
                            {availableFields.length === 0 && (
                                <li className="text-gray-400 italic">Aucun champ trouvé</li>
                            )}
                        </ul>
                    </div>

                    {/* Champs à exporter */}
                    <div className="w-1/2 border rounded p-4">
                        <h4 className="font-semibold mb-2">Champs à exporter</h4>
                        <ul className="max-h-48 overflow-auto text-sm">
                            {selectedFields.map(field => (
                                <li
                                    key={field.value}
                                    className="flex justify-between items-center py-1 border-b"
                                >
                                    <span>{field.label}</span>
                                    <button
                                        className="text-red-500 hover:text-red-700"
                                        onClick={() => handleRemoveField(field.value)}
                                    >
                                        <FaTrash />
                                    </button>
                                </li>
                            ))}
                            {selectedFields.length === 0 && (
                                <li className="text-gray-400 italic">Aucun champ sélectionné</li>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-4">
                    <button
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded shadow"
                        onClick={onCancel}
                    >
                        Annuler
                    </button>
                    <button
                        className={`flex items-center gap-2 bg-[--color-primary] text-white px-4 py-2 rounded-lg shadow disabled:opacity-50`}
                        onClick={() => onExport(selectedFields.map(f => f.value), format)}
                        disabled={loading || selectedFields.length === 0}
                    >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaFileExport />}
                        {loading ? "Export..." : "Exporter"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ExportModal;
